(() => {
  const labels = document.currentScript?.dataset || {};
  const label = labels.inStockLabel || 'In stock';
  const rootSelector = '#cm_results, #cm-collection-page';
  const cardSelector = '.cm_SearchPage .cm_SearchResult .card-wrapper';
  const badgeSelector = '[data-fr-stock-badge]';
  const observed = new WeakSet();
  let scheduled = false;
  const sortStates = new Map();

  function resultSignature(root) {
    return JSON.stringify([...root.querySelectorAll('.cm_SearchResult .cmRepeater_items > *')]
      .map((item) => item.querySelector('.card__heading a')?.getAttribute('href')));
  }

  function messageFor(root, selectors) {
    return [...root.querySelectorAll('#itemsBlock > .cm_message')].flatMap((message) => [...message.querySelectorAll(selectors)])
      .find((element) => {
        for (let node = element; node && node !== root; node = node.parentElement) {
          const style = getComputedStyle(node);
          if (node.hidden || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      });
  }
  const failureSelector = '.cmTemplate_badRequest, .cmTemplate_unknown, .cmTemplate_timeout, .cmTemplate_networkError, .cm_message__usage-limit-exceeded';

  function sortStatus(container) {
    let status = container.querySelector('.fr-sort-status');
    if (!status) {
      status = document.createElement('span');
      status.className = 'fr-sort-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      container.append(status);
    }
    return status;
  }

  function stopSort(root) {
    const state = sortStates.get(root);
    if (!state) return;
    clearTimeout(state.timer);
    state.status.textContent = '';
    sortStates.delete(root);
  }

  function syncSort(root) {
    const select = root.querySelector('select.cm_sort-select');
    const container = select?.closest('.cm_sort');
    if (container && labels.sortPending) sortStatus(container);
    const state = sortStates.get(root);
    if (!state) return;
    if (!root.isConnected || !container || select.value !== state.value) {
      stopSort(root);
      return;
    }
    if (!container.contains(state.status)) {
      container.querySelector('.fr-sort-status')?.remove();
      container.append(state.status);
    }
    if (!state.pending) {
      if (!state.superseded && root.querySelector('.cm_SearchResult > .cm_loading')) stopSort(root);
      return;
    }
    if (root.querySelector('.cm_SearchResult > .cm_loading')) { state.loaded = true; return; }
    const failure = messageFor(root, failureSelector);
    const empty = messageFor(root, '.cmTemplate_nothing');
    const changed = resultSignature(root) !== state.signature || state.loaded && state.itemsMutated;
    if (!changed && failure === state.failure && empty === state.empty) return;
    if (failure) state.status.textContent = labels.sortError;
    else if (empty) state.status.textContent = labels.sortEmpty;
    else if (changed && root.querySelector('.cm_SearchResult .card__heading a')) {
      state.status.textContent = state.superseded ? labels.sortUnconfirmed : labels.sortUpdated.replace('[sort]', select.selectedOptions[0]?.textContent.trim() || '');
    } else return;
    clearTimeout(state.timer);
    state.pending = false;
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('select.cm_sort-select');
    const root = event.target.closest?.(rootSelector);
    if (!root) return;
    const previous = sortStates.get(root);
    const superseded = previous?.pending === true || previous?.superseded === true;
    stopSort(root);
    if (!select || !labels.sortPending) return;
    observer.takeRecords();
    const status = sortStatus(select.closest('.cm_sort'));
    const state = {
      value: select.value, status, pending: true, superseded, signature: resultSignature(root),
      failure: messageFor(root, failureSelector), empty: messageFor(root, '.cmTemplate_nothing'),
      loaded: !!root.querySelector('.cm_SearchResult > .cm_loading'), itemsMutated: false,
    };
    sortStates.set(root, state);
    select.closest('.cm_sort')?.append(status);
    queueMicrotask(() => {
      if (sortStates.get(root) === state && state.pending) status.textContent = labels.sortPending;
    });
    state.timer = setTimeout(() => {
      if (sortStates.get(root) === state && state.pending) status.textContent = labels.sortUnconfirmed;
    }, 15000);
    schedule();
  }, true);

  document.addEventListener('click', (event) => {
    const root = event.target.closest?.(rootSelector);
    if (root && !event.target.closest('.cm_sort')) stopSort(root);
  }, true);
  window.addEventListener('popstate', () => [...sortStates.keys()].forEach(stopSort));

  const categoryPanels = new Map();
  const categoryFields = ['category', 'product_type'];

  function categorySnapshot(root, response, request) {
    if (!request || response?.error || response?.query !== request.query ||
        root.querySelector('.cm_SearchResult > .cm_loading')) return null;
    if (!Array.isArray(request.selection)) return null;
    const selected = request.selection.filter((entry) => categoryFields.includes(entry.field));
    if (!selected.length) return null;
    const names = selected.map(({ field, term }) => {
      const accepted = response?.facets?.get?.(field)?.selection;
      return Array.isArray(accepted) ? accepted.find((entry) => entry.term === term)?.value : undefined;
    });
    if (names.some((name) => typeof name !== 'string' || !name.trim())) return null;
    return { key: JSON.stringify(selected.map(({ field, term }) => [field, term])), title: [...new Set(names)].join(' · '), request };
  }

  function categoryPending(state, pending) {
    clearTimeout(state.timer);
    state.pending = pending;
    state.form.setAttribute('aria-busy', String(pending));
    state.button.disabled = pending;
    state.status.textContent = pending ? state.panel.dataset.loading : '';
    if (pending) state.timer = setTimeout(() => categoryPending(state, false), 10000);
  }

  function removeCategory(root) {
    const state = categoryPanels.get(root);
    if (!state) return;
    clearTimeout(state.timer);
    state.panel.remove();
    categoryPanels.delete(root);
  }

  function syncCategoryHint(state) {
    const text = state.input.value !== (state.query || '') && state.panel.dataset.draftHint
      ? state.panel.dataset.draftHint : state.appliedHint;
    if (state.hint.textContent !== text) state.hint.textContent = text;
  }

  function createCategory(root, template) {
    const panel = template.content.firstElementChild.cloneNode(true);
    const form = panel.querySelector('form');
    const input = form.elements.query;
    const title = panel.querySelector('h2');
    const label = form.querySelector('label');
    const hint = form.querySelector('.fr-category-context__hint');
    title.id = `fr-category-title-${root.id}`;
    input.id = `fr-category-query-${root.id}`;
    label.htmlFor = input.id;
    panel.setAttribute('aria-labelledby', title.id);
    const state = { panel, form, input, title, label, hint, appliedHint: hint.textContent, button: form.querySelector('button'), status: form.querySelector('[role="status"]'), dirty: false };
    form.addEventListener('input', () => { state.dirty = true; categoryPending(state, false); syncCategoryHint(state); });
    form.addEventListener('submit', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (state.pending) return;
      if (!input.value.trim()) { input.value = ''; syncCategoryHint(state); input.reportValidity(); return; }
      try {
        const provider = window.Convermax;
        const current = categorySnapshot(root, provider?.getSearchResponse?.(), provider?.getSearchRequest?.());
        if (!current || current.key !== state.key || typeof provider?.createSearchUrl !== 'function') throw new Error();
        const request = { ...current.request, selection: current.request.selection.map((entry) => ({ ...entry })), query: input.value, pageNumber: 0 };
        const url = provider.createSearchUrl(request);
        if (typeof url !== 'string' || !url) throw new Error();
        const destination = new URL(url, location.href);
        if (destination.origin !== location.origin || destination.username || destination.password) throw new Error();
        categoryPending(state, true);
        window.location.assign(destination.href);
      } catch {
        categoryPending(state, false);
        state.status.textContent = panel.dataset.error;
      }
    });
    root.before(panel);
    categoryPanels.set(root, state);
    return state;
  }

  function syncCategory(root, response, request) {
    const template = document.getElementById('FRCategoryContextTemplate');
    if (!template) return;
    const category = categorySnapshot(root, response, request);
    let state = categoryPanels.get(root);
    if (!category) {
      if (state) {
        const waiting = !response?.error && (root.querySelector('.cm_SearchResult > .cm_loading') ||
          Array.isArray(request?.selection) && request.selection.some((entry) => categoryFields.includes(entry.field)));
        state.panel.hidden = !waiting;
        state.panel.style.visibility = waiting ? 'hidden' : '';
        categoryPending(state, false);
      }
      return;
    }
    state ||= createCategory(root, template);
    state.panel.hidden = false;
    state.panel.style.visibility = '';
    if (state.key && (state.key !== category.key || state.query !== request.query) && document.activeElement !== state.input) state.dirty = false;
    state.key = category.key;
    state.query = request.query;
    state.title.textContent = category.title;
    state.label.textContent = state.panel.dataset.label.replace('[category]', category.title);
    state.form.setAttribute('aria-label', state.label.textContent);
    if (!state.dirty && document.activeElement !== state.input) state.input.value = request.query || '';
    syncCategoryHint(state);
  }

  function resetCategories() {
    for (const state of categoryPanels.values()) { state.dirty = false; categoryPending(state, false); }
    schedule();
  }
  window.addEventListener('pageshow', resetCategories);
  window.addEventListener('popstate', resetCategories);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const root of categoryPanels.keys()) if (event.target.contains(root)) removeCategory(root);
  });

  function productPath(value) {
    if (!value) return null;
    try {
      return new URL(value, location.origin).pathname.match(/\/products\/[^/]+/)?.[0] || null;
    } catch {
      return null;
    }
  }

  function productsByKey(response) {
    const products = new Map();
    try {
      if (response?.error) return products;
      const items = response?.items;
      // Convermax returns a collection with map(), not an indexed array.
      items?.map?.((entry) => {
        const item = entry?.plainObject || entry;
        if (!item) return;
        // "Available" also includes backorders; only this flag means in stock.
        const inStock = Array.isArray(item.stock) && item.stock.includes('In-stock Only');
        const displayedPrice = item.on_sale ? item.price : item.min_price || item.price;
        const product = { inStock, hasZeroPrice: displayedPrice === 0 };
        if (item.id != null) products.set(String(item.id), product);
        const path = productPath(item.url);
        if (path) products.set(path, product);
      });
    } catch {
      // Hide labels if the app is not ready or changes its response format.
      products.clear();
    }
    return products;
  }

  function syncZeroPrice(card, product) {
    const current = card.querySelector('.price--on-sale .price__sale > .price-item--sale:not(.price-item--set), .price:not(.price--on-sale) .price__regular > .price-item--regular:not(.price-item--set)');
    card.querySelectorAll('[data-fr-zero-price]').forEach((span) => {
      if (product?.hasZeroPrice && span === current) return;
      if (span.textContent === span.dataset.frZeroPrice) span.textContent = '';
      delete span.dataset.frZeroPrice;
    });
    if (labels.zeroPrice && product?.hasZeroPrice && current && !current.textContent.trim()) {
      current.textContent = labels.zeroPrice;
      current.dataset.frZeroPrice = labels.zeroPrice;
    }
  }

  // Convermax owns the filter portal and all filter mutations. Supply only the
  // missing button semantics, keyboard click fallback, and opening/return focus.
  const filterOpener = '.cm_SearchPage .cm_SearchHeader .cm_dialogButton';
  const filterActions = '#cm_FacetDialog .cm_facet-dialog_button-clear, #cm_FacetDialog .cm_facet-dialog_button-done';
  let filterSession, filterGesture;
  const visibleControl = (element) => element?.isConnected && element.getClientRects().length > 0;
  const filterContent = () => document.querySelector('#cm_FacetDialog > .dialog-container');

  function enhanceFilterButton(control) {
    if (control.matches('button, input, a[href]')) return;
    if (!control.hasAttribute('role')) control.setAttribute('role', 'button');
    if (!control.hasAttribute('tabindex')) control.tabIndex = 0;
    control.dataset.frFilterButton = '';
  }

  function syncFilters() {
    const dialog = document.getElementById('cm_FacetDialog');
    const content = filterContent();
    document.querySelectorAll(filterOpener).forEach((control) => {
      enhanceFilterButton(control);
      control.setAttribute('aria-haspopup', 'dialog');
      control.setAttribute('aria-controls', 'cm_FacetDialog');
      control.setAttribute('aria-expanded', String(!!content));
    });
    document.querySelectorAll(filterActions).forEach(enhanceFilterButton);
    if (content) {
      const title = dialog.querySelector('.dialog-title');
      if (!dialog.hasAttribute('role')) {
        dialog.setAttribute('role', 'dialog');
        dialog.dataset.frFilterRole = '';
      }
      if (title) {
        title.id ||= 'FRFacetDialogTitle';
        if (!dialog.hasAttribute('aria-labelledby') && !dialog.hasAttribute('aria-label')) {
          dialog.setAttribute('aria-labelledby', title.id);
          dialog.dataset.frFilterTitle = title.id;
        }
      }
      if (filterSession && !filterSession.open) {
        clearTimeout(filterSession.timer);
        filterSession.open = true;
        filterSession.dialog = dialog;
        const active = document.activeElement;
        if (dialog.contains(active)) filterSession.focusWithin = true;
        else if (active === filterSession.opener || active === filterSession.previousActive || active === document.body) {
          // Once per opening. Do not compete with later provider facet focus.
          const target = title || dialog.querySelector('.cm_facet-dialog_button-done');
          if (target && visibleControl(target)) {
            if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
            target.focus({ preventScroll: true });
            filterSession.focusWithin = dialog.contains(document.activeElement);
          }
        }
      }
    } else {
      if (dialog?.hasAttribute('data-fr-filter-role')) {
        if (dialog.getAttribute('role') === 'dialog') dialog.removeAttribute('role');
        delete dialog.dataset.frFilterRole;
      }
      if (dialog?.hasAttribute('data-fr-filter-title')) {
        if (dialog.getAttribute('aria-labelledby') === dialog.dataset.frFilterTitle) dialog.removeAttribute('aria-labelledby');
        delete dialog.dataset.frFilterTitle;
      }
      if (filterSession?.open) {
        const session = filterSession;
        filterSession = null;
        const active = document.activeElement;
        const opener = visibleControl(session.opener) ? session.opener :
          [...session.root.querySelectorAll(filterOpener)].find(visibleControl);
        if (session.focusWithin && (active === document.body || session.dialog.contains(active)) && opener) opener.focus({ preventScroll: true });
      }
    }
    if (dialog) observer.observe(dialog, observerOptions);
    // Some provider versions create the BODY portal only on first use. Observe
    // only direct children while waiting, and stop after ten seconds if absent.
    else if (filterSession && !filterSession.open) observer.observe(document.body, { childList: true });
  }

  document.addEventListener('focusin', (event) => {
    if (filterSession?.open) filterSession.focusWithin = filterSession.dialog.contains(event.target);
  });

  document.addEventListener('click', (event) => {
    if (filterGesture?.control.contains(event.target)) filterGesture.handled = true;
    const opener = event.target.closest?.(filterOpener);
    if (!opener || opener.getAttribute('aria-disabled') === 'true' || opener.hasAttribute('disabled')) return;
    if (!filterContent()) {
      clearTimeout(filterSession?.timer);
      const session = { opener, root: opener.closest(rootSelector) || opener.closest('.cm_SearchPage'), previousActive: document.activeElement };
      filterSession = session;
      session.timer = setTimeout(() => {
        if (filterSession !== session || session.open) return;
        filterSession = null;
        schedule();
      }, 10000);
      const dialog = document.getElementById('cm_FacetDialog');
      observer.observe(dialog || document.body, dialog ? observerOptions : { childList: true });
    }
    schedule();
  }, true);

  function filterControl(event) {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey ||
        !['Enter', ' '].includes(event.key)) return null;
    const control = event.target.closest?.('[data-fr-filter-button]');
    return control && control.getAttribute('aria-disabled') !== 'true' && !control.hasAttribute('disabled') ? control : null;
  }

  document.addEventListener('keydown', (event) => {
    if (event.repeat) return;
    const control = filterControl(event);
    filterGesture = control ? { control, key: event.key, keydown: event, url: location.href, content: filterContent() } : null;
  }, true);
  document.addEventListener('keydown', (event) => {
    if (filterGesture && filterControl(event) === filterGesture.control && event.key === filterGesture.key) {
      if (event.defaultPrevented) filterGesture.handled = true;
      else if (event.key === ' ') {
        event.preventDefault();
        if (event === filterGesture.keydown) filterGesture.preventedScroll = true;
      }
    }
    if (event.key !== 'Escape' || event.repeat || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const dialog = event.target.closest?.('#cm_FacetDialog');
    const content = filterContent();
    if (!dialog || !content) return;
    queueMicrotask(() => {
      if (event.defaultPrevented || filterContent() !== content) return;
      const done = dialog.querySelector('.cm_facet-dialog_button-done');
      if (done && visibleControl(done) && done.getAttribute('aria-disabled') !== 'true' && !done.hasAttribute('disabled')) done.click();
    });
  });
  document.addEventListener('keyup', (event) => {
    const current = filterGesture;
    if (!current || event.repeat || filterControl(event) !== current.control || event.key !== current.key) return;
    queueMicrotask(() => {
      if (filterGesture !== current) return;
      filterGesture = null;
      if (event.defaultPrevented || current.handled || (current.keydown.defaultPrevented && !current.preventedScroll) ||
          !current.control.isConnected || location.href !== current.url || filterContent() !== current.content) return;
      current.control.click();
    });
  });

  const observerOptions = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['href', 'id', 'aria-label'] };
  const observer = new MutationObserver((records) => {
    for (const [root, state] of sortStates) {
      if (!state.pending) continue;
      state.itemsMutated ||= records.some(({ target, type, addedNodes, removedNodes }) => {
        const element = target.nodeType === 1 ? target : target.parentElement;
        if (!root.contains(element)) return false;
        return element.matches('.cm_SearchResult .cmRepeater_items') ||
          element.closest('.cm_SearchResult .price-item, .cm_SearchResult .card__heading') ||
          type === 'childList' && [...addedNodes, ...removedNodes].some((node) => node.nodeType === 1 && node.matches('.cmRepeater_items'));
      });
    }
    schedule();
  });

  function sync() {
    scheduled = false;
    let response, request;
    try {
      response = window.Convermax?.getSearchResponse?.();
      if (document.getElementById('FRCategoryContextTemplate')) request = window.Convermax?.getSearchRequest?.();
    } catch {}
    const products = productsByKey(response);
    // Keep our own label insertions out of the observer queue.
    observer.disconnect();
    for (const root of sortStates.keys()) if (!root.isConnected) stopSort(root);
    for (const root of categoryPanels.keys()) if (!root.isConnected) removeCategory(root);
    document.querySelectorAll(rootSelector).forEach((root) => {
      syncCategory(root, response, request);
      syncSort(root);
      root.querySelectorAll(cardSelector).forEach((card) => {
        // Let the action's own text name it instead of the provider's placeholder.
        card.querySelectorAll('.cm_product-item__buttons-form a.cm_button[aria-label="button"]').forEach((action) => {
          if (action.textContent.trim()) action.removeAttribute('aria-label');
        });
        const id = card.querySelector('.cm_product-item__buttons-form')?.getAttribute('id');
        const path = productPath(card.querySelector('.card__heading a, a.card')?.getAttribute('href'));
        const product = id && products.has(id) ? products.get(id) : products.get(path);
        syncZeroPrice(card, product);
        const inStock = product?.inStock === true;
        const badge = card.querySelector(badgeSelector);
        if (!inStock) {
          badge?.remove();
          return;
        }
        const container = card.querySelector('.card__badge');
        if (!container || badge) return;
        const element = document.createElement('span');
        element.className = 'badge badge--in-stock';
        element.dataset.frStockBadge = '';
        element.textContent = label;
        container.append(element);
      });
      observer.observe(root, observerOptions);
    });
    syncFilters();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(sync);
  }

  function initialize() {
    document.querySelectorAll(rootSelector).forEach((root) => {
      if (observed.has(root)) return;
      observed.add(root);
      observer.observe(root, observerOptions);
      schedule();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();

// The provider's category anchors have no href or native keyboard activation.
// Reuse their click action without touching the separate mobile filter dialog.
(() => {
  const selector = '.cm_SearchPage > .cm_FacetPanel .cm_field-category a.facetentrykey:not([href])';
  let gesture;

  function controlFor(event) {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return null;
    if (event.key !== 'Enter' && event.key !== ' ') return null;
    const control = event.target.closest?.(selector);
    return control?.getAttribute('aria-disabled') === 'true' ? null : control;
  }

  function recoverFocusAfterClick(control) {
    if (!control.contains(document.activeElement)) return;
    const page = control.closest('.cm_SearchPage');
    const root = page.closest('#cm_results, #cm-collection-page') || page;
    const visible = (element) => element?.isConnected && element.getClientRects().length > 0;
    const stop = () => {
      observer.disconnect();
      clearTimeout(expiry);
      document.removeEventListener('pointerdown', stop, true);
      document.removeEventListener('keydown', stop, true);
      document.removeEventListener('focusin', onFocus, true);
      window.removeEventListener('blur', stop);
    };
    const onFocus = (event) => {
      if (event.target !== control && event.target !== document.body) stop();
    };
    const observer = new MutationObserver(() => {
      const active = document.activeElement;
      if (active === control && visible(control)) return;
      if (active && active !== document.body && active !== control) {
        stop();
        return;
      }
      // A cleared mobile category panel may now be hidden. The existing Sort
      // select is the next native catalog control; do not invent a new button.
      const target = [...root.querySelectorAll(
        '.cm_SearchPage > .cm_FacetPanel .cm_field-category .cm_filterInput, .cm_SearchPage select.cm_sort-select'
      )].find((element) => !element.disabled && visible(element));
      if (!target) return;
      stop();
      target.focus();
    });
    // This only bounds focus observation, never a provider request or mutation.
    const expiry = setTimeout(stop, 10000);
    document.addEventListener('pointerdown', stop, true);
    document.addEventListener('keydown', stop, true);
    document.addEventListener('focusin', onFocus, true);
    window.addEventListener('blur', stop);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
  }

  document.addEventListener('keydown', (event) => {
    if (event.repeat) return;
    const control = controlFor(event);
    gesture = control ? {
      control, key: event.key, keydown: event, url: location.href,
      row: control.closest('.facetdiv'),
      selected: control.closest('.facetdiv')?.classList.contains('selected'),
    } : null;
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!gesture || controlFor(event) !== gesture.control || event.key !== gesture.key) return;
    if (event.defaultPrevented) gesture.handled = true;
    else if (event.key === ' ') {
      // Cancel page scrolling only after the provider has received the key.
      event.preventDefault();
      if (event === gesture.keydown) gesture.preventedScroll = true;
    }
  });

  document.addEventListener('click', (event) => {
    if (gesture?.row?.contains(event.target)) gesture.handled = true;
  }, true);

  document.addEventListener('keyup', (event) => {
    const current = gesture;
    if (!current || event.repeat || controlFor(event) !== current.control || event.key !== current.key) return;
    // Let provider key handlers finish first, including handlers on window.
    queueMicrotask(() => {
      if (gesture !== current) return;
      gesture = null;
      if (event.defaultPrevented || current.handled ||
          (current.keydown.defaultPrevented && !current.preventedScroll) ||
          !current.control.isConnected || !current.control.matches(selector) ||
          location.href !== current.url || current.row?.classList.contains('selected') !== current.selected) return;
      recoverFocusAfterClick(current.control);
      current.control.click();
    });
  });
})();
